/**
 * PONTE nominal → indexado do PnL dos socios — ESPELHO TS do #345.
 *
 * PARIDADE OBRIGATORIA com o backend (fonte da doutrina):
 *   - `indexadoDe`  == cortex/services/pnl_diario_telegram.py::indexado_de
 *   - `pontePnl`    == o render da ponte da mensagem Telegram (mesma unidade
 *     nas 3 linhas; Nominal e Indexado = valor VERDADEIRO arredondado; residuo
 *     de arredondamento absorvido na linha da correcao IPCA — "plug" contabil,
 *     a soma exibida FECHA sempre), incluindo: corte de unidade ROUNDING-AWARE
 *     (999.600 → "+R$ 1,0 mi", nunca "1000 mil"), zero SEM sinal, e
 *     half-away-from-zero (o ROUND_HALF_UP do Decimal do Python; o
 *     Math.round cru do JS arredonda -2,5 → -2, DIFERENTE — por isso o
 *     `halfAway`).
 *
 * O CX-INV-13 guarda o contrato no STORE (ipca_pendente ⇔ fv_*_ipca null;
 * coluna == payload); Telegram e app derivam o indexado por ESTA soma → os
 * tres concordam por construcao. Mudou a regra la, muda AQUI junto.
 *
 * LIMITE conhecido (review #345): componente menor que 1 passo de exibicao
 * pode sair "0,0" (ou com o sinal do residuo) — so a linha do meio absorve.
 */

/** indexado = nominal + correcao IPCA. IPCA `null` = PENDENTE → null —
 * NUNCA soma 0 no lugar (zero-cego: null e "nao sei"; 0.0 e "implicita nao
 * mexeu"). Mesma assinatura moral do `indexado_de` do backend. */
export function indexadoDe(
  nominal: number | null | undefined,
  ipca: number | null | undefined,
): number | null {
  if (nominal === null || nominal === undefined) return null;
  if (ipca === null || ipca === undefined) return null;
  return nominal + ipca;
}

/** Arredonda pro inteiro mais proximo com empate PRA LONGE DO ZERO —
 * paridade com o ROUND_HALF_UP do Decimal (Python). */
function halfAway(x: number): number {
  return Math.sign(x) * Math.round(Math.abs(x));
}

interface Unidade {
  passo: number; // R$ por passo inteiro de exibicao (100k = 0,1 mi; 1k; 1)
  decimais: number; // casas na renderizacao (passos/10 no "mi")
  sufixo: string;
}

/** Unidade UNICA pelo maior |v| do conjunto, com corte ROUNDING-AWARE (meio
 * passo antes da fronteira — espelho do `_unidade` do backend). */
function unidadeDe(...valores: Array<number | null>): Unidade {
  const m = Math.max(0, ...valores.filter((v): v is number => v !== null).map(Math.abs));
  if (m >= 1_000_000 - 500) return { passo: 100_000, decimais: 1, sufixo: " mi" };
  if (m >= 1_000 - 0.5) return { passo: 1_000, decimais: 0, sufixo: " mil" };
  return { passo: 1, decimais: 0, sufixo: "" };
}

/** `q` passos inteiros → "+R$ 2,4 mi" / "-R$ 261 mil" / "R$ 0,0 mi" (zero SEM
 * sinal — negativo minusculo que quantiza a zero nao ganha "+" nem "-0,0"). */
function fmtPassos(q: number, u: Unidade): string {
  const sinal = q === 0 ? "" : q < 0 ? "-" : "+";
  const abs = Math.abs(q);
  const txt = u.decimais === 1
    ? `${Math.floor(abs / 10)},${abs % 10}`
    : String(abs);
  return `${sinal}R$ ${txt}${u.sufixo}`;
}

export interface PontePnl {
  nominal: string;
  ipca: string;
  indexado: string;
  /** valores quantizados em R$ (pro corValor/sinal na UI) */
  nominalQ: number;
  ipcaQ: number;
  indexadoQ: number;
}

/** As 3 linhas da ponte, FECHANDO por construcao (aritmetica em PASSOS
 * INTEIROS de exibicao — sem residuo de float): nomQ + ipcaQ == indQ sempre.
 * `null` quando o indexado nao e computavel (IPCA pendente / nominal ausente)
 * — o caller renderiza a pendencia, nunca soma. */
export function pontePnl(
  nominal: number | null | undefined,
  ipca: number | null | undefined,
): PontePnl | null {
  const ind = indexadoDe(nominal, ipca);
  if (ind === null) return null;
  const nom = nominal as number;
  const u = unidadeDe(nom, ipca as number, ind);
  const nomQ = halfAway(nom / u.passo);
  const indQ = halfAway(ind / u.passo);
  const ipcaQ = indQ - nomQ; // plug: fecha por construcao
  return {
    nominal: fmtPassos(nomQ, u),
    ipca: fmtPassos(ipcaQ, u),
    indexado: fmtPassos(indQ, u),
    nominalQ: nomQ * u.passo,
    ipcaQ: ipcaQ * u.passo,
    indexadoQ: indQ * u.passo,
  };
}

/** Valor avulso (fora da ponte) na propria unidade — mesma regra de corte e
 * sinal ("Resultado do dia" do Telegram usa o equivalente). */
export function brlPonteSolto(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  const u = unidadeDe(v);
  return fmtPassos(halfAway(v / u.passo), u);
}
